import React, { useCallback, useEffect, useState } from 'react';
import { Umbrella, CalendarPlus, Inbox, Ban } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LeaveRequestModal from './LeaveRequestModal';

const formatDate = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('pl-PL') : '');

const fmtDays = (d) => {
  if (d == null) return '–';
  const v = Math.round(d * 100) / 100;
  return `${v} ${v === 1 ? 'dzień' : 'dni'}`;
};

const fmtHours = (h) => {
  if (!h) return '0 h';
  const m = Math.round(h * 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${hh} h` : `${hh}:${String(mm).padStart(2, '0')} h`;
};

export const STATUS_STYLES = {
  OCZEKUJE_NA_PRZELOZONEGO: 'bg-violet-100 text-violet-800',
  ZLOZONY: 'bg-amber-100 text-amber-800',
  ANULOWANY: 'bg-gray-100 text-gray-500',
  ODRZUCONY: 'bg-red-100 text-red-700',
  ZATWIERDZONY_DO_WPISU: 'bg-blue-100 text-blue-800',
  WPISANY_RECZNIE: 'bg-emerald-100 text-emerald-800',
  ZAPISYWANIE: 'bg-blue-100 text-blue-800',
  ZAPISANY_W_ZZL: 'bg-emerald-100 text-emerald-800',
  ZAPISANY_BEZ_PRZELICZENIA: 'bg-amber-100 text-amber-800',
  BLAD_ZAPISU: 'bg-red-100 text-red-700',
};

/** Bilans urlopu z KS-ZZL, przycisk wniosku i lista własnych wniosków (strona "Mój grafik"). */
const LeaveSection = () => {
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        api.get('/kadry/urlopy/me/balance'),
        api.get('/kadry/urlopy/me'),
      ]);
      setBalance(b.data);
      setRequests(r.data || []);
    } catch (err) {
      setBalance({ linked: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try {
      await api.post(`/kadry/urlopy/me/${id}/cancel`);
      toast.success('Wniosek anulowany');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się anulować wniosku');
    }
  };

  // Konto niepowiązane z kadrami: komunikat pokazuje już sam grafik - tu nic nie dublujemy.
  if (!balance || balance.linked === false) return null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
              <Umbrella className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">Bilans urlopu wypoczynkowego (KS-ZZL)</div>
              {balance.contracts && balance.contracts.length > 0 ? (
                balance.contracts.map((c) => (
                  <div key={c.idUmp} className="mt-0.5">
                    <span className="text-2xl font-bold text-gray-900">Pozostało {fmtDays(c.pozostalyDni)}</span>
                    <span className="text-sm text-gray-500 ml-2">({fmtHours(c.pozostalyGodz)})</span>
                    <div className="text-xs text-gray-500 mt-1">
                      Należny {fmtDays(c.naleznyDni)} · wykorzystany {fmtDays(c.wykorzystanyDni)}
                      {c.pozostalyZaleglyDni > 0 && <> · w tym zaległy do wykorzystania {fmtDays(c.pozostalyZaleglyDni)}</>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-600 mt-0.5">{balance.message || 'Brak danych o bilansie.'}</div>
              )}
            </div>
          </div>
          <button onClick={() => setModalOpen(true)} className="btn btn-primary flex items-center gap-2 self-start sm:self-center">
            <CalendarPlus className="w-4 h-4" /> Złóż wniosek urlopowy
          </button>
        </div>
      </div>

      {requests.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <Inbox className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-800">Moje wnioski urlopowe</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {requests.map((r) => (
              <div key={r.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {r.kindName}: {formatDate(r.dateFrom)} – {formatDate(r.dateTo)}
                    {r.daysEstimate != null && <span className="text-gray-500 font-normal"> · {r.daysEstimate} dni rob.</span>}
                  </div>
                  {r.waitingForName && (
                    <div className="text-xs text-violet-700 mt-0.5">Czeka na podpis: {r.waitingForName}</div>
                  )}
                  {r.supervisorSignedByName && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Podpisał: {r.supervisorSignedByName}
                      {r.supervisorOnBehalfOfName ? ` (w zastępstwie ${r.supervisorOnBehalfOfName})` : ''}
                    </div>
                  )}
                  {(r.decisionComment || r.supervisorComment || r.comment) && (
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {r.decisionComment ? `Decyzja: ${r.decisionComment}`
                        : r.supervisorComment ? `Przełożony: ${r.supervisorComment}` : r.comment}
                    </div>
                  )}
                </div>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-700'}`}>
                  {r.statusLabel}
                </span>
                {(r.status === 'ZLOZONY' || r.status === 'OCZEKUJE_NA_PRZELOZONEGO') && (
                  <button onClick={() => cancel(r.id)} className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1">
                    <Ban className="w-3.5 h-3.5" /> Anuluj
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <LeaveRequestModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSubmitted={load} />
    </div>
  );
};

export default LeaveSection;
